import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import { OverviewDetailsProps } from '../Overview/interfaces/interface';
import TenantLists from '../TenantLists';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Button } from '../../ui/button';
import {
  Download,
  FileTerminal,
  FileText,
  Monitor,
  RefreshCw,
  Search,
  User,
  ChevronDown,
  ChevronRight,
  CalendarIcon,
} from 'lucide-react';
import { Input } from '../../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Calendar } from '../../ui/calendar';
import { format } from 'date-fns';
import { cn } from '../../../utils/utils';
import { Card, CardContent } from '../../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { myLocalStorage } from '../../StorageHelper';
import { fetchAuditLogsApi } from '../Lolbin/Api/lolbinapi';
import useUserStore from '../../../services/userStore';
import { Badge } from '../../ui/badge';
import { Skeleton } from '../../ui/skeleton';
import { handleGeneratePDF } from '../DownloadGenerate';
import CloudEntityActivityLogs from '../CloudAnalytics/CloudEntityActivityLogs';
import DownloadReportDialog from '../Alerts/DownloadReportDialog';

export default function AuditLogsDetails({
  onSelectorChange,
  fromdetails = false,
  computerName = '',
  computeremail = '',
  computerId = ''
}: OverviewDetailsProps) {
  const [selectedTime, setSelectedTime] = useState<string>('lw');
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(undefined);
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [lastFetchedTime, setLastFetchedTime] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [containerHeight, setContainerHeight] = useState<number>(600);
  // Download report dialog state
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [includeAlertJson, setIncludeAlertJson] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Handler for confirming download
  const handleDownloadReport = () => {
    setIsDownloading(true);
    const logsForReport = auditLogs.map(log => {
      if (includeAlertJson) {
        let eventJson = log?.eventJson;
        if (typeof eventJson === 'string') {
          try {
            eventJson = JSON.parse(eventJson);
          } catch {
            // leave as string
          }
        }
        return { ...log, alertJson: eventJson };
      } else {
        return { ...log };
      }
    });
    handleGeneratePDF(
      logsForReport,
      [null, null],
      searchQuery
    );
    setIsDownloading(false);
    setDownloadDialogOpen(false);
  };

  const tableRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const tenantName = myLocalStorage.getItem('latestTenant')?.tenantName || '';
  const { email } = useUserStore((state) => state.user);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (!value.trim() && appliedSearchQuery !== '') {
      setAppliedSearchQuery('');
      setLastFetchedTime(null);
      setHasMore(true);
      setPage(1);
      fetchAuditLogs(true, false, '');
    }
  };
  const handleSearchClick = () => {
    const query = searchQuery.trim();
    setAppliedSearchQuery(query);
    setLastFetchedTime(null);
    setHasMore(true);
    setPage(1);
    fetchAuditLogs(true, false, query);
  };

  const toggleRowExpansion = (index: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getLogTypePriority = (type: string) => {
    if (type.toLowerCase().includes('blocked')) {
      return 'high';
    }

    switch (type) {
      case 'Enforce':
      case 'Alert':
      case 'AppInstall':
        return 'high';
      case 'LOLBin Audit':
      case 'Resource Control':
        return 'medium';
      case 'Audit':
      default:
        return 'low';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };


  const getBadgeClassByPriority = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-red-500 text-red-600 dark:border-red-400 dark:text-red-400';
      case 'medium':
        return 'border-yellow-500 text-yellow-600 dark:border-yellow-400 dark:text-yellow-400';
      case 'low':
      default:
        return 'border-green-500 text-green-600 dark:border-green-400 dark:text-green-400';
    }
  };
  const currentFetchController = useRef<AbortController | null>(null);
  const currentRequestId = useRef(0);
  const fetchAuditLogs = async (reset = false, refreshing = false, searchText = appliedSearchQuery) => {
    const bucket = 'ondevice-auditlog';
    const cursorTime = reset || refreshing ? '' : lastFetchedTime || '';
    const requestedPage = reset ? 1 : page || 1;
    const requestId = ++currentRequestId.current;
    setIsLoading(true);
    if (currentFetchController.current) {
      currentFetchController.current.abort();
    }
    const controller = new AbortController();
    currentFetchController.current = controller;

    // Determine if custom mode is active
    const customMode = customStartDate && customEndDate;

    try {
      const response = await fetchAuditLogsApi(
        bucket,
        tenantName,
        customMode ? 'custom' : selectedTime,
        computerName,
        computerId,
        computeremail,
        searchText,
        email,
        fromdetails,
        cursorTime,
        "",
        requestedPage,
        200,
        controller.signal,
        customMode ? format(customStartDate, 'yyyy-MM-dd') : undefined,
        customMode ? format(customEndDate, 'yyyy-MM-dd') : undefined
      );

      const logs = Array.isArray(response) ? response : [];

      // If API returns no rows, stop pagination and don't append
      if (logs.length === 0) {
        if (reset) setAuditLogs([]);
        setHasMore(false);
        return;
      }

      const sortedLogs = [...logs].sort(
        (a, b) => new Date(b._time).getTime() - new Date(a._time).getTime()
      );

      setAuditLogs((prevLogs) =>
        reset ? sortedLogs : [...prevLogs, ...sortedLogs]
      );

      const lastTime = sortedLogs[sortedLogs.length - 1]?._time;
      const firstTime = sortedLogs[0]?._time;

      if (lastTime && lastTime !== lastFetchedTime) {
        setLastFetchedTime(lastTime);
      }
    } catch (error: any) {
      // Don't clear data if request was aborted (user is typing fast)
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        console.log('Request aborted - user changed search criteria');
      } else {
        console.error('Error fetching audit logs:', error);
        setHasMore(false);
      }
    } finally {
      if (requestId === currentRequestId.current) {
        setIsLoading(false);
      }
    }
  };


  useEffect(() => {
    if (email && tenantName) {
      setLastFetchedTime(null);
      setHasMore(true);
      setPage(1);
      fetchAuditLogs(true, false, appliedSearchQuery);
    }
  }, [selectedTime, appliedSearchQuery, email, tenantName, customStartDate, customEndDate]);

  const handleTableScroll = () => {
    if (tableRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = tableRef.current;

      // Check if content height is less than or equal to visible height (no scrollbar yet)
      const contentFitsInView = scrollHeight <= clientHeight;

      // Check if near bottom (within 100px of bottom)
      const nearBottom = scrollHeight - scrollTop <= clientHeight + 100;


      // Load more if: content fits in view OR user scrolled near bottom
      if ((contentFitsInView || nearBottom) && hasMore && !isLoading) {
        setPage((page) => page + 1);
        fetchAuditLogs();
      }
    }
  };

  useEffect(() => {
    const ref = tableRef.current;
    if (ref) ref.addEventListener('scroll', handleTableScroll);

    return () => {
      if (ref) ref.removeEventListener('scroll', handleTableScroll);
    };
  }, [hasMore, isLoading, auditLogs]);

  // Trigger load if content doesn't fill the screen on initial load
  useEffect(() => {
    if (tableRef.current && auditLogs.length > 0) {
      const { scrollHeight, clientHeight } = tableRef.current;
      if (scrollHeight <= clientHeight && hasMore && !isLoading) {
        setPage((page) => page + 1);
        fetchAuditLogs();
      }
    }
  }, [auditLogs, hasMore, isLoading]);

  // Calculate dynamic height based on viewport
  useEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const availableHeight = viewportHeight - rect.top - 32; // 32px for bottom padding
        setContainerHeight(Math.max(400, availableHeight)); // Minimum 400px
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, [fromdetails]);

  return (
    <div className="p-6 space-y-6">
      {!fromdetails &&

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 rounded-xl flex items-center justify-center border border-purple-200 dark:border-purple-700">
              <FileTerminal className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="text-platform-logs-title">
                Device Audit Logs
              </h1>
              <p className="text-muted-foreground">
                View and analyze user activity logs from endpoints, servers
              </p>
            </div>
          </div>
        </div>

      }



      <div className="flex items-center justify-between pb-4 border-b">

        {/* Filters and Search */}
        <div className="flex items-center gap-4">
          {!fromdetails && <TenantLists onSelectorChange={onSelectorChange} isLabelNeeded={true} />}

          <div className="flex items-center gap-2">
            <Select defaultValue="lw" onValueChange={(value) => {
              setSelectedTime(value);
              if (value !== 'custom') {
                setCustomStartDate(undefined);
                setCustomEndDate(undefined);
              }
            }}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24Hrs">Last 24 Hours</SelectItem>
                <SelectItem value="lw">Last 7 Days</SelectItem>
                <SelectItem value="lm">Last 30 Days</SelectItem>
                <SelectItem value="l3m">Last 90 Days</SelectItem>
                <SelectItem value="custom">Custom Date Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedTime === 'custom' && (
            <>
              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-40 justify-start text-left font-normal",
                        !customStartDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {customStartDate ? format(customStartDate, "MMM dd") : "Start date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={customStartDate}
                      onSelect={(date) => {
                        setCustomStartDate(date);
                        if (customEndDate && date && customEndDate <= date) {
                          setCustomEndDate(undefined);
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-40 justify-start text-left font-normal",
                        !customEndDate && "text-muted-foreground"
                      )}
                      disabled={!customStartDate}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {customEndDate ? format(customEndDate, "MMM dd") : "End date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={customEndDate}
                      onSelect={setCustomEndDate}
                      initialFocus
                      disabled={(date) => {
                        if (!customStartDate) return true;
                        return date <= customStartDate;
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <div className="relative">

              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchClick();
                  }
                }}
                className="w-64 pr-10"
                data-testid="input-search-audit"
              />
              <button
                type="button"
                onClick={handleSearchClick}
                className="absolute transform -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Search"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setDownloadDialogOpen(true)}>
            <Download className="w-4 h-4" />
            Download Report
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              setLastFetchedTime(null);
              setHasMore(true);
              setPage(1);
              fetchAuditLogs(true, true);
            }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        <DownloadReportDialog
          open={downloadDialogOpen}
          onOpenChange={setDownloadDialogOpen}
          onConfirm={handleDownloadReport}
          onCancel={() => setDownloadDialogOpen(false)}
          loading={isDownloading}
          includeAlertJson={includeAlertJson}
          setIncludeAlertJson={setIncludeAlertJson}
          title="Do you want to download the Audit logs report?"
          subtitle="Include event JSON in PDF"
        />
      </div>

      {/* Audit Logs Table */}
      <Card ref={containerRef} className="overflow-hidden border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 ">
        <CardContent className="p-0">
          <div ref={tableRef} style={{ maxHeight: `${containerHeight}px` }} className="overflow-auto">
            <Table className="w-full table-fixed">
              <TableHeader className="sticky top-0 z-10">
                <TableRow className="bg-gradient-to-r from-slate-700 to-slate-800 dark:from-slate-800 dark:to-slate-900 hover:from-slate-700 hover:to-slate-800 dark:hover:from-slate-800 dark:hover:to-slate-900 border-b border-slate-600 dark:border-slate-700">
                  <TableHead className="w-[5%] text-white font-semibold"></TableHead>
                  <TableHead className="w-[10%] text-white font-semibold">Timestamp</TableHead>
                  <TableHead className="w-[15%] text-white font-semibold">User</TableHead>
                  <TableHead className="w-[15%] text-white font-semibold">Computer</TableHead>
                  <TableHead className="w-[10%] text-white font-semibold">Event Type</TableHead>
                  <TableHead className="w-[45%] text-white font-semibold">Resource</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log, index) => {
                  const date = formatTimestamp(log?._time);
                  const priority = getLogTypePriority(log?.resourceType || log?.type || '');
                  const isHighPriority = priority === 'high';
                  const isExpanded = expandedRows[index];

                  return (
                    <React.Fragment key={index}>
                      <TableRow
                        className={`border-b border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer transition-all duration-150 ${index % 2 === 0
                          ? 'bg-white dark:bg-gray-900'
                          : 'bg-gray-50 dark:bg-gray-800/50'
                          } ${isHighPriority ? 'border-l-4 border-red-500 dark:border-red-400' : ''}`}
                      >
                        <TableCell onClick={() => toggleRowExpansion(index)}>
                          <button className="p-1.5 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                            )}
                          </button>
                        </TableCell>

                        <TableCell>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{date}</span>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-start gap-2">
                            <div className="p-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 mt-0.5">
                              <User className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex flex-col min-w-0 gap-1">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                                {log?.computerUserEmail?.split('\\').pop() || log?.user || '-'}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {log?.computerName?.includes('\\')
                                  ? log.computerName.split('\\')[0]
                                  : ''}
                              </span>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                              <Monitor className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {log?.computerName || log?.computer || '-'}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell align="center">
                          <Badge variant="outline" className={`px-2 text-sm font-medium break-words whitespace-normal ${getBadgeClassByPriority(priority)}`}
                          >
                            {log?.resourceType || log?.type || '-'}
                          </Badge>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="font-mono text-sm font-medium leading-relaxed break-all text-gray-900 dark:text-gray-100">
                              {log?.resourceName || log?.resource || '-'}
                            </span>
                            {log?.message && (
                              <div className="pl-3 mt-1 text-sm leading-relaxed border-l-2 border-gray-400 dark:border-gray-600 text-gray-600 dark:text-gray-400 break-words whitespace-pre-line block">
                                {log.message}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow
                          className={`${index % 2 === 0
                            ? 'bg-blue-50/50 dark:bg-gray-800'
                            : 'bg-blue-50/30 dark:bg-gray-900'
                            } border-b border-gray-200 dark:border-gray-700`}
                        >
                          <TableCell colSpan={6} className="p-4">
                            <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 rounded-lg border border-gray-200 dark:border-gray-700">
                              <h4 className="mb-3 text-sm font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
                                <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                Event JSON
                              </h4>
                              {(() => {
                                let parsed = {};

                                try {
                                  parsed = log?.eventJson ? JSON.parse(log.eventJson) : {};
                                } catch (e) {
                                  parsed = {};
                                }

                                const isEmpty =
                                  !parsed ||
                                  (typeof parsed === "object" && Object.keys(parsed).length === 0);

                                return isEmpty ? (
                                  <span className="text-sm text-muted-foreground">No data available.</span>
                                ) : (
                                  <CloudEntityActivityLogs data={parsed} />
                                );
                              })()}

                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Loading Skeleton */}
                {isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`} className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton className="w-full h-4 bg-gray-200 dark:bg-gray-700" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
